import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./GovernanceRequirements.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const GovernanceRequirements = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const { formData, setFormData, loading, hydrated, fetching, editable, setEditable, handleDone } =
    useAccreditation({
      labId,
      section: "Structural & governance requirements",
      name: "SADCAS F134-A",
      auditorId: user._id,
      isAssessor: user.role === "ASSESSOR",
      isEditing,
      token,
    });

  const handleAnswerChange = (questionId, score, comment, points, content) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "Is the laboratory or the organization of which the laboratory is a part, an entity that can be held legally responsible for its activities? NOTE: A government laboratory is deemed to be a legal entity on the basis of its government status.",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions2: [
        {
          content: "Laboratory director competence",
          number: "5.2.1",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "Is the laboratory directed by a person, or persons however named, with the specified qualifications, competence, delegated authority, responsibility, and resources to fulfil the requirements of ISO 15189:2022?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          content: "Laboratory director responsibilities",
          number: "5.2.2",
          hideOptions: true,
        },
        {
          questionId: "Q3",
          content:
            "Is the laboratory director responsible for the implementation of the management system, including the application of risk management to all aspects of the laboratory operations so that risks to patient care and opportunities to improve are systematically identified and addressed?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q4",
          content:
            "Are the duties and responsibilities of the laboratory director documented?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          content: "Delegation of duties",
          number: "5.2.3",
          hideOptions: true,
        },
        {
          questionId: "Q5",
          content:
            "Has the laboratory director delegated either selected duties or responsibilities, or both, to qualified and competent personnel and is such delegation documented?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q6",
          content:
            "Does the laboratory director maintain the ultimate responsibility for the overall operation of the laboratory?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions3: [
        {
          content: "General",
          number: "5.3.1",
          hideOptions: true,
        },
        {
          questionId: "Q7",
          content:
            "Does the laboratory specify and document the range of laboratory activities, including laboratory activities performed at sites other than the main location (e.g., POCT, sample collection) for which it conforms with ISO 15189:2022",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          content: "Conformance with requirements",
          number: "5.3.2",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content:
            "Does the laboratory carry out its activities in such a way as to meet the requirements of ISO 15189:2022, the users, regulatory authorities and organizations providing recognition?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q9",
          content:
            "Does this apply to the complete range of specified and documented laboratory activities, regardless of where the service is provided?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          content: "Advisory activities",
          number: "5.3.3",
          hideOptions: true,
        },
        {
          questionId: "Q10",
          content:
            "How does the laboratory management ensure that appropriate laboratory advice and interpretation are available, and does it meet the needs of patients and users?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q11",
          content:
            "What arrangements has the laboratory established for communicating with laboratory users on the following when applicable: a) advising on choice and use of examinations, including required type of sample, clinical indications and limitations of examination methods, and the frequency of requesting the examination;",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q12",
          content:
            "What arrangements has the laboratory established for communicating with laboratory users on the following when applicable: b) providing professional judgments on the interpretation of the results of examinations;",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q13",
          content:
            "What arrangements has the laboratory established for communicating with laboratory users on the following when applicable: c) promoting the effective utilization of laboratory examinations;",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q14",
          content:
            "What arrangements has the laboratory established for communicating with laboratory users on the following when applicable: d) advising on scientific and logistical matters such as instances of failure of sample(s) to meet acceptability criteria.;",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions4: [
        {
          content: "General",
          number: "5.4.1",
          hideOptions: true,
        },
        {
          questionId: "Q15",
          content:
            "Has the laboratory: a) defined its organization and management structure, its place in any parent organization, and the relationships between management, technical operations and support services?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q16",
          content:
            "Is the laboratory director responsible for the implementation of the management system, including the application of risk management to all aspects of the laboratory operations so that risks to patient care and opportunities to improve are systematically identified and addressed?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q17",
          content:
            "b) specified the responsibility, authority, lines of communication and interrelationship of all personnel who manage, perform or verify work affecting the results of laboratory activities?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q18",
          content:
            "c) specified its procedures to the extent necessary to ensure the consistent application of its laboratory activities and the validity of the results?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          content: "Quality management",
          number: "5.4.2",
          hideOptions: true,
        },
        {
          questionId: "Q19",
          content:
            "Does the laboratory have personnel who, irrespective of other responsibilities, have the authority and resources needed to carry out their duties, including? a) Implementation, maintenance and improvement of the management system",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q20",
          content:
            "b) identification of deviations from the management system or from the procedures for performing laboratory activities?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q21",
          content:
            "c) initiation of actions to prevent or minimize such deviations?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q22",
          content:
            "d) reporting to laboratory management on the performance of the management system and any need for improvement?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q23",
          content: "e) ensuring the effectiveness of laboratory activities?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions5: [
        {
          questionId: "Q24",
          content:
            "a) Has the laboratory management established, and do they maintain objectives and policies to: 1) meet the needs and requirements of its patients and users; 2) commit to good professional practice; 3) provide examinations that fulfil their intended use; 4) conform to ISO 15189:2022.",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q25",
          content:
            "b) Are objectives measurable, and consistent with policies? How does the laboratory ensure that the objectives and policies are implemented at all levels of the laboratory organization?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q26",
          content:
            "c) How does the laboratory management ensure that the integrity of the management system is maintained when changes to the management system are planned and implemented?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q27",
          content:
            "d) Has the laboratory established quality indicators to evaluate performance throughout key aspects of pre-examination, examination, and post-examination processes and monitor performance in relation to objectives?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions6: [
        {
          questionId: "Q28",
          content:
            "a) Has laboratory management established, implemented, and maintained processes for identifying risks of harm to patients and opportunities for improved patient care associated with its examinations and activities, and have actions been developed to address both risks and opportunities for improvement?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q29",
          content:
            "b) Does the laboratory director ensure that these processes are evaluated for effectiveness and modified, when identified as being ineffective?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            number="5.1"
            title="Legal Entity"
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions1} // refer the array here
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="5.2"
            title="Laboratory Director"
            accreditation
            showPointsField={false}
            addWidth={true}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="5.3"
            title="Laboratory Activities"
            accreditation
            addWidth={true}
            showPointsField={false}
            questions={questionSets[0].questions3}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="5.4"
            title="Structure and Authority"
            accreditation
            addWidth={true}
            showPointsField={false}
            questions={questionSets[0].questions4}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="5.5"
            title="Objectives and Policies"
            accreditation
            showPointsField={false}
            addWidth={true}
            questions={questionSets[0].questions5}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="5.6"
            title="Risk Management"
            accreditation
            showPointsField={false}
            addWidth={true}
            questions={questionSets[0].questions6}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
    // Add more sections here if needed
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">
            Clause 5: Structural & governance requirements
          </h2>
        </div>

        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => { setIsEditing(true); setEditable(true); }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS A - Clause 5: Structural & governance requirements has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, formData)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default GovernanceRequirements;